/**
 * Session context for a business admin uploading a file on behalf of a tradie
 * (from the Tradies tab or a project tradie profile). Stored in localStorage
 * and optionally mirrored on the route query so navigation between /templates
 * and /file routes does not lose the guard context.
 */

export function readTradieUploadContext(route) {
  try {
    const storedTradieId = localStorage.getItem('tradie_id')
    if (storedTradieId && storedTradieId !== 'undefined') {
      const storedProjectId = localStorage.getItem('project_id')
      return {
        tradieId: storedTradieId,
        projectId:
          storedProjectId && storedProjectId !== 'undefined'
            ? storedProjectId
            : null,
      }
    }

    const query = route && route.query
    const queryTradieId = query && query.tradie_id
    if (queryTradieId && queryTradieId !== 'undefined') {
      const queryProjectId = query && query.project_id
      return {
        tradieId: String(queryTradieId),
        projectId:
          queryProjectId && queryProjectId !== 'undefined'
            ? String(queryProjectId)
            : null,
      }
    }
  } catch (e) {
    // ignore storage errors
  }

  return null
}

export function hasTradieUploadContext(route) {
  return readTradieUploadContext(route) !== null
}

export function persistTradieUploadContext(source = {}) {
  const tradieId = source.tradieId || source.tradie_id
  const projectId = source.projectId || source.project_id

  try {
    if (tradieId && tradieId !== 'undefined') {
      localStorage.setItem('tradie_id', String(tradieId))
    }
    if (projectId && projectId !== 'undefined') {
      localStorage.setItem('project_id', String(projectId))
    }
  } catch (e) {
    // ignore storage errors
  }
}

export function tradieUploadContextQuery() {
  const ctx = readTradieUploadContext()
  if (!ctx) return {}

  const query = { tradie_id: ctx.tradieId }
  if (ctx.projectId) query.project_id = ctx.projectId
  return query
}

// Routes that are still part of a tradie's upload flow (profile -> templates
// -> form -> file view/edit -> back). Moving between them must keep the
// stored tradie_id/project_id; leaving to anything else must drop it, or a
// later company upload (e.g. typing /file/create) is saved against the last
// profile that was opened.
const UPLOAD_FLOW_ROUTE_NAMES = [
  'tradies.view-tradie',
  'projects.view-tradie',
  'templates.list-page',
  'template.form-page',
  'file.new-page',
  'file.view-page',
  'file.edit-page',
  'request.request-file-new-page',
  'request.request-file-template-form-page',
]

export function isTradieUploadFlowRoute(route) {
  return !!route && UPLOAD_FLOW_ROUTE_NAMES.includes(route.name)
}

export function clearTradieUploadContext() {
  try {
    localStorage.removeItem('tradie_id')
    localStorage.removeItem('project_id')
  } catch (e) {
    // ignore storage errors
  }
}

export function clearTradieUploadContextUnlessInFlow(route) {
  if (!isTradieUploadFlowRoute(route)) clearTradieUploadContext()
}
